import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageProvider";
import { IMAGES } from "@/lib/brand";
import { fetchJobs, submitApplication } from "@/lib/api";
import { Briefcase, MapPin, Upload, Check, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

export const Careers = () => {
  const { t, lang } = useLang();
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    gdpr: false,
  });
  const [cv, setCv] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchJobs().then(setJobs).catch(() => setJobs([]));
  }, []);

  const apply = (job) => {
    setSelectedJob(job);
    const el = document.getElementById("apply-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.gdpr) {
      toast.error(t.careers.form.gdpr);
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone || "");
      fd.append("message", form.message || "");
      fd.append("gdpr_consent", "true");
      fd.append("language", lang);
      if (selectedJob) {
        fd.append("job_id", selectedJob.id);
        fd.append("job_title", selectedJob.title[lang] || selectedJob.title.de);
      } else {
        fd.append("job_title", t.careers.form.initiative);
      }
      if (cv) fd.append("cv", cv);

      await submitApplication(fd);
      toast.success(t.careers.form.success);
      setForm({ name: "", email: "", phone: "", message: "", gdpr: false });
      setCv(null);
      setSelectedJob(null);
    } catch (err) {
      console.error(err);
      toast.error(t.careers.form.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="careers"
      data-testid="careers-section"
      className="relative scroll-mt-24 overflow-hidden bg-white py-24 dark:bg-[#020617] md:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        {/* Header with image */}
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-[#7C3AED]/20 bg-[#7C3AED]/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#7C3AED]">
              {t.careers.eyebrow}
            </div>
            <h2 className="mt-5 font-display text-[clamp(2rem,4.2vw,3.25rem)] font-bold leading-tight tracking-tight text-[#0F172A] dark:text-white">
              {t.careers.title}
            </h2>
            <p className="mt-4 max-w-xl text-base text-slate-600 dark:text-slate-300 md:text-lg">
              {t.careers.subtitle}
            </p>

            <div className="mt-8">
              <div className="font-display text-sm font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {t.careers.benefits_title}
              </div>
              <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                {t.careers.benefits.map((b, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                    <Check className="h-4 w-4 text-[#7C3AED]" strokeWidth={3} />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-3xl shadow-elevated ring-1 ring-black/5">
              <img src={IMAGES.careers} alt="" className="h-[460px] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0F172A]/70 via-transparent to-[#7C3AED]/20" />
            </div>
          </motion.div>
        </div>

        {/* Open positions */}
        <div className="mt-20">
          <h3 className="font-display text-2xl font-bold tracking-tight text-[#0F172A] dark:text-white md:text-3xl">
            {t.careers.open_positions}
          </h3>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {jobs.map((j) => (
              <motion.div
                key={j.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                data-testid="job-listing-item"
                className="group rounded-3xl border border-slate-200/70 bg-white p-6 transition-all hover:border-[#7C3AED]/40 hover:-translate-y-1 hover:shadow-elevated dark:border-white/5 dark:bg-[#0F172A]"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="inline-flex items-center gap-2 rounded-full bg-[#1E3A8A]/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#1E3A8A] dark:bg-white/5 dark:text-[#A5B4FC]">
                      <Briefcase className="h-3 w-3" />
                      {(j.type[lang] || j.type.de)}
                    </div>
                    <h4 className="mt-3 font-display text-xl font-bold text-[#0F172A] dark:text-white">
                      {j.title[lang] || j.title.de}
                    </h4>
                    <div className="mt-1 flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400">
                      <MapPin className="h-3.5 w-3.5" />
                      {j.location}
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                      {j.summary[lang] || j.summary.de}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => apply(j)}
                  data-testid={`apply-${j.id}`}
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#7C3AED] transition-all hover:gap-2"
                >
                  {t.careers.apply_now}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Application Form */}
        <div id="apply-form" className="mt-20 scroll-mt-24">
          <div className="relative overflow-hidden rounded-3xl border border-slate-200/70 bg-gradient-to-br from-white via-white to-[#F8FAFC] p-8 shadow-elevated md:p-12 dark:border-white/5 dark:from-[#0F172A] dark:via-[#0F172A] dark:to-[#0B1020]">
            <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-[#7C3AED]/20 blur-3xl" />
            <div className="relative grid gap-8 lg:grid-cols-[1fr_1.4fr]">
              <div>
                <h3 className="font-display text-2xl font-bold tracking-tight text-[#0F172A] dark:text-white md:text-3xl">
                  {t.careers.form.title}
                </h3>
                <p className="mt-3 text-sm text-slate-600 dark:text-slate-300 md:text-base">
                  {t.careers.form.subtitle}
                </p>
                {selectedJob && (
                  <div className="mt-5 rounded-2xl border border-[#7C3AED]/30 bg-[#7C3AED]/5 p-4">
                    <div className="text-xs uppercase tracking-wider text-[#7C3AED]">
                      {t.careers.form.position}
                    </div>
                    <div className="mt-1 font-display font-bold text-[#0F172A] dark:text-white">
                      {selectedJob.title[lang] || selectedJob.title.de}
                    </div>
                    <button
                      onClick={() => setSelectedJob(null)}
                      className="mt-2 text-xs text-slate-500 underline"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>

              <form onSubmit={submit} data-testid="application-form" className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Input
                      required
                      data-testid="app-name"
                      placeholder={t.careers.form.name}
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="h-12 rounded-xl"
                    />
                  </div>
                  <div>
                    <Input
                      required
                      type="email"
                      data-testid="app-email"
                      placeholder={t.careers.form.email}
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="h-12 rounded-xl"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Input
                      type="tel"
                      data-testid="app-phone"
                      placeholder={t.careers.form.phone}
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="h-12 rounded-xl"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Textarea
                      data-testid="app-message"
                      placeholder={t.careers.form.message}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      rows={5}
                      className="rounded-xl"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label
                      htmlFor="cv-upload"
                      className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border-2 border-dashed border-slate-300 bg-white px-4 py-3 transition-all hover:border-[#7C3AED]/50 dark:border-white/10 dark:bg-white/5"
                    >
                      <div className="flex items-center gap-3">
                        <Upload className="h-5 w-5 text-[#7C3AED]" />
                        <div className="text-sm">
                          <div className="font-medium text-slate-700 dark:text-slate-200">
                            {t.careers.form.cv}
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">
                            {cv ? cv.name : t.careers.form.cv_none}
                          </div>
                        </div>
                      </div>
                      <span className="rounded-lg bg-[#0F172A] px-3 py-1.5 text-xs font-semibold text-white dark:bg-white dark:text-[#0F172A]">
                        {t.careers.form.cv_choose}
                      </span>
                    </label>
                    <input
                      id="cv-upload"
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => setCv(e.target.files?.[0] || null)}
                      data-testid="cv-upload-input"
                      className="hidden"
                    />
                  </div>
                </div>

                <div className="flex items-start gap-3 pt-2">
                  <Checkbox
                    id="app-gdpr"
                    checked={form.gdpr}
                    onCheckedChange={(v) => setForm({ ...form, gdpr: !!v })}
                    data-testid="app-gdpr"
                    className="mt-0.5"
                  />
                  <label htmlFor="app-gdpr" className="text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                    {t.careers.form.gdpr}
                  </label>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-application-button"
                  className="h-12 w-full rounded-xl bg-gradient-to-r from-[#1E3A8A] to-[#7C3AED] text-white shadow-glow-purple transition-all hover:scale-[1.01]"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t.careers.form.submit}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Careers;
